import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import {
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import { db } from "../../configs/firebase";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";
import AppPhoneInput, { AppPhoneCountryCode, buildInternationalNumber } from "../Shared/PhoneInput";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import {
  normalizeUserRole,
  USER_ROLES,
} from "../Profile/constants/userRoles";
type Status = "draft" | "hiring" | "paused" | "closed";
type WorkMode = "remote" | "hybrid" | "onsite";
type EmploymentType =
  | "full-time"
  | "part-time"
  | "contract"
  | "internship";
type ApplicationStatus =
  | "new"
  | "reviewing"
  | "shortlisted"
  | "interview"
  | "rejected"
  | "hired";
interface Vacancy {
  _id: string;
  title: string;
  department: string;
  location: string;
  workMode: WorkMode;
  employmentType: EmploymentType;
  status: Status;
  summary: string;
  compensation: string;
  responsibilities: string[];
  requirements: string[];
  workArrangement: string;
  postedAt: string | null;
  closingDate: string | null;
  createdAt: string;
}
interface Application {
  _id: string;
  vacancyTitle: string;
  fullName: string;
  email: string;
  phone: string;
  coverLetter: string;
  cvName: string;
  createdAt: string;
  status: ApplicationStatus;
}
interface PageData<T> {
  items: T[];
  page: number;
  total: number;
  pages: number;
  counts?: Record<string, number>;
}
interface FormValues {
  title: string;
  department: string;
  location: string;
  workMode: WorkMode;
  employmentType: EmploymentType;
  status: Status;
  summary: string;
  compensation: string;
  responsibilities: string;
  requirements: string;
  workArrangement: string;
  closingDate: string;
}
interface ApplicantValues {
  fullName: string;
  email: string;
  phone: string;
  coverLetter: string;
}
interface ApiMessage {
  success: boolean;
  message?: string;
  confirmationSent?: boolean;
}
export interface CareersPageProps {
  mode?: "public" | "admin";
  apiBaseUrl?: string;
  getAdminToken?: () => Promise<string>;
}
const BRAND = "#14b8a6";
const BRAND_DARK = "#086e65";
const BG = "#f7fbfc";
const BORDER = "#dce5e8";
const RADIUS = "5px";
const MAX_CV_SIZE = 15 * 1024 * 1024;
const ALLOWED_CV = /\.(pdf|doc|docx|odt|rtf|txt|jpe?g|png)$/i;
const INITIAL_FORM: FormValues = {
  title: "",
  department: "Sales",
  location: "Malawi",
  workMode: "remote",
  employmentType: "full-time",
  status: "draft",
  summary: "",
  compensation: "",
  responsibilities: "",
  requirements: "",
  workArrangement: "",
  closingDate: "",
};
const INITIAL_APPLICANT: ApplicantValues = {
  fullName: "",
  email: "",
  phone: "",
  coverLetter: "",
};
const emptyPage = <T,>(): PageData<T> => ({
  items: [],
  page: 1,
  total: 0,
  pages: 0,
});
const dateLabel = (value?: string | null): string => {
  if (!value) return "Not specified";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not specified"
    : date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};
const normalizeList = (value: string): string[] =>
  value
    .split(/\r?\n/)
    .map((line) =>
      line.replace(/^\s*(?:[-*•–]\s*|\d+[.)]\s+)/, "").trim()
    )
    .filter(Boolean);
const toLines = (items: string[] = []): string =>
  items.map((item) => `- ${item}`).join("\n");
const statusColor = (status: Status): string => {
  const colors: Record<Status, string> = {
    hiring: "green",
    draft: "gray",
    paused: "orange",
    closed: "red",
  };
  return colors[status];
};
const formatBytes = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.ceil(bytes / 1024)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
const cardStyle = {
  bg: "white",
  border: "1px solid",
  borderColor: BORDER,
  borderRadius: RADIUS,
};
const SectionTitle: React.FC<{
  number: string;
  title: string;
  description: string;
}> = ({ number, title, description }) => (
  <Flex gap={3} align="start" mb={4}>
    <Flex
      w="32px"
      h="32px"
      bg="#e7f5f2"
      color={BRAND}
      borderRadius={RADIUS}
      justify="center"
      align="center"
      fontWeight="bold"
      fontSize="sm"
      flexShrink={0}
    >
      {number}
    </Flex>
    <Box flex="1">
      <Heading size="sm">{title}</Heading>
      <Text fontSize="xs" color="gray.500" mt={1}>
        {description}
      </Text>
    </Box>
  </Flex>
);
const CareersLoader: React.FC<{ label?: string }> = ({ label = "Loading opportunities..." }) => (
  <Flex minH="260px" w="100%" align="center" justify="center" direction="column" gap={3} role="status" aria-live="polite">
    <GlobalSpinner />
    <Text fontSize="sm" color="gray.600">{label}</Text>
  </Flex>
);
const Pagination: React.FC<{
  page: number;
  pages: number;
  change: (page: number) => void;
}> = ({ page, pages, change }) => {
  if (pages <= 1) return null;
  return (
    <Flex
      align="center"
      justify="center"
      gap={3}
      mt={5}
      flexWrap="wrap"
    >
      <Button
        size="sm"
        variant="outline"
        isDisabled={page <= 1}
        onClick={() => change(page - 1)}
      >
        Previous
      </Button>
      <Text fontSize="sm" color="gray.600">
        Page {page} of {pages}
      </Text>
      <Button
        size="sm"
        variant="outline"
        isDisabled={page >= pages}
        onClick={() => change(page + 1)}
      >
        Next
      </Button>
    </Flex>
  );
};
export default function CareersPage({
  mode = "public",
  apiBaseUrl = "",
  getAdminToken,
}: CareersPageProps) {
  const toast = useToast();
  const { theme } = useCustomTheme();
  const [applicantPhoneCountryCode, setApplicantPhoneCountryCode] = useState<AppPhoneCountryCode>("MW");
  const [applicantPhoneTouched, setApplicantPhoneTouched] = useState(false);
  const modal = useDisclosure();
  const uploadRef = useRef<HTMLInputElement | null>(null);
  const isAdminView = mode === "admin";
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [roleChecked, setRoleChecked] = useState(false);
  const canManage = roleChecked && isSuperAdmin;
  const [vacancies, setVacancies] = useState<PageData<Vacancy>>(
    emptyPage<Vacancy>()
  );
  const [applications, setApplications] =
    useState<PageData<Application>>(emptyPage<Application>());
  const [page, setPage] = useState(1);
  const [applicationPage, setApplicationPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [vacanciesLoaded, setVacanciesLoaded] = useState(false);
  const [vacanciesError, setVacanciesError] = useState(false);
  const [applicationLoading, setApplicationLoading] = useState(true);
  const [applicationsLoaded, setApplicationsLoaded] = useState(false);
  const [applicationsError, setApplicationsError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Vacancy | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormValues>({
    ...INITIAL_FORM,
  });
  const [applicant, setApplicant] = useState<ApplicantValues>({
    ...INITIAL_APPLICANT,
  });
  const [cv, setCv] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(
      getAuth(),
      async (user) => {
        if (!active) return;
        setIsSuperAdmin(false);
        setRoleChecked(false);
        if (!user) {
          setRoleChecked(true);
          return;
        }
        try {
          const snapshot = await getDoc(
            doc(db, "users", user.uid)
          );
          const role = snapshot.exists()
            ? normalizeUserRole(snapshot.data().role)
            : USER_ROLES.GUEST;
          if (active) {
            setIsSuperAdmin(role === USER_ROLES.SUPER_ADMIN);
          }
        } catch (error) {
          console.error("[CAREERS] Role verification:", error);
          if (active) setIsSuperAdmin(false);
        } finally {
          if (active) setRoleChecked(true);
        }
      }
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);
  const notify = useCallback(
    (
      title: string,
      status: "success" | "error" | "warning",
      description?: string
    ) => {
      toast({
        title,
        description,
        status,
        duration: status === "success" ? 5500 : 6500,
        isClosable: true,
        position: "bottom-right",
      });
    },
    [toast]
  );
  const reportError = useCallback(
    (error: unknown) => {
      notify(
        error instanceof Error ? error.message : "Unexpected error.",
        "error"
      );
    },
    [notify]
  );
  const api = useCallback(
    async <T,>(
      path: string,
      options: RequestInit = {},
      protectedRequest = false
    ): Promise<T> => {
      const headers = new Headers(options.headers);
      if (protectedRequest) {
        const user = getAuth().currentUser;
        const token = getAdminToken
          ? await getAdminToken()
          : user
            ? await user.getIdToken()
            : null;
        if (!token) {
          throw new Error("Please sign in as Super Admin.");
        }
        headers.set("Authorization", `Bearer ${token}`);
      }
      const configuredBase =
        apiBaseUrl ||
        process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_API_URL ||
        (process.env.NODE_ENV === "production"
          ? "https://api.pickdengu.mw"
          : "http://localhost:5000");
      const base = configuredBase.replace(/\/+$/, "");
      const response = await fetch(
        `${base}/api/v1/careers${path}`,
        {
          ...options,
          headers,
        }
      );
      const result: unknown = await response.json().catch(() => ({}));
      const message =
        result &&
        typeof result === "object" &&
        "message" in result &&
        typeof result.message === "string"
          ? result.message
          : `Request failed (${response.status}).`;
      if (!response.ok) throw new Error(message);
      return result as T;
    },
    [apiBaseUrl, getAdminToken]
  );
  const loadVacancies = useCallback(async () => {
    if (isAdminView && !canManage) return;
    setLoading(true);
    setVacanciesError(false);
    setVacanciesLoaded(false);
    try {
      const path = isAdminView
        ? `/admin/vacancies?page=${page}${
            filter ? `&status=${encodeURIComponent(filter)}` : ""
          }`
        : `/vacancies?page=${page}`;
      const result = await api<PageData<Vacancy>>(
        path,
        {},
        isAdminView
      );
      if (!result || !Array.isArray(result.items)) {
        throw new Error(
          "Vacancies API returned an invalid response (missing items array). Check the backend URL and API response."
        );
      }
      setVacancies({
        ...result,
        items: result.items,
        page: Number(result.page) || 1,
        total: Number(result.total) || 0,
        pages: Number(result.pages) || 0,
      });
      setVacanciesLoaded(true);
    } catch (error) {
      setVacanciesError(true);
      reportError(error);
    } finally {
      setLoading(false);
    }
  }, [api, canManage, filter, isAdminView, page, reportError]);
  useEffect(() => {
    // Public jobs can load immediately; admin requests wait for authorization.
    if (!isAdminView || roleChecked) void loadVacancies();
  }, [isAdminView, loadVacancies, roleChecked]);
  const loadApplications = useCallback(async () => {
    if (!isAdminView || !canManage) return;
    setApplicationLoading(true);
    setApplicationsError(false);
    setApplicationsLoaded(false);
    try {
      const result = await api<PageData<Application>>(
        `/admin/applications?page=${applicationPage}`,
        {},
        true
      );
      if (!result || !Array.isArray(result.items)) {
        throw new Error(
          "Applications API returned an invalid response (missing items array)."
        );
      }
      setApplications({
        ...result,
        items: result.items,
        page: Number(result.page) || 1,
        total: Number(result.total) || 0,
        pages: Number(result.pages) || 0,
      });
      setApplicationsLoaded(true);
    } catch (error) {
      setApplicationsError(true);
      reportError(error);
    } finally {
      setApplicationLoading(false);
    }
  }, [
    api,
    applicationPage,
    canManage,
    isAdminView,
    reportError,
  ]);
  useEffect(() => {
    if (roleChecked) void loadApplications();
  }, [loadApplications, roleChecked]);
  const update = <K extends keyof FormValues>(
    field: K,
    value: FormValues[K]
  ) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };
  const resetForm = () => {
    setForm({ ...INITIAL_FORM });
    setEditingId(null);
  };
  const openCreate = () => {
    if (!canManage) return;
    resetForm();
    modal.onOpen();
  };
  const editVacancy = (job: Vacancy) => {
    if (!canManage) return;
    setEditingId(job._id);
    setForm({
      title: job.title,
      department: job.department || "",
      location: job.location || "",
      workMode: job.workMode,
      employmentType: job.employmentType,
      status: job.status,
      summary: job.summary || "",
      compensation: job.compensation || "",
      responsibilities: toLines(job.responsibilities),
      requirements: toLines(job.requirements),
      workArrangement: job.workArrangement || "",
      closingDate: job.closingDate
        ? job.closingDate.substring(0, 10)
        : "",
    });
    modal.onOpen();
  };
  const closeModal = () => {
    if (saving) return;
    modal.onClose();
    resetForm();
  };
  const saveVacancy = async (draft: boolean) => {
    if (!canManage || saving) return;
    if (!form.title.trim()) {
      notify("Please enter a position title.", "warning");
      return;
    }
    const status: Status = draft ? "draft" : form.status;
    if (status === "hiring" && !form.summary.trim()) {
      notify("Add a role overview before publishing.", "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        status,
        responsibilities: normalizeList(form.responsibilities),
        requirements: normalizeList(form.requirements),
        closingDate: form.closingDate || null,
      };
      const path = editingId
        ? `/admin/vacancies/${editingId}`
        : "/admin/vacancies";
      await api<ApiMessage>(
        path,
        {
          method: editingId ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        true
      );
      notify(
        draft
          ? "Draft saved successfully"
          : editingId
            ? "Vacancy updated successfully"
            : "Vacancy created successfully",
        "success",
        draft
          ? "You can return and finish this vacancy later."
          : status === "hiring"
            ? "Your vacancy is now live."
            : "Changes saved."
      );
      modal.onClose();
      resetForm();
      if (page === 1) {
        await loadVacancies();
      } else {
        setPage(1);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setSaving(false);
    }
  };
  const changeStatus = async (job: Vacancy, status: Status) => {
    if (!canManage) return;
    try {
      await api<ApiMessage>(
        `/admin/vacancies/${job._id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        },
        true
      );
      notify(`Vacancy changed to ${status}`, "success");
      await loadVacancies();
    } catch (error) {
      reportError(error);
    }
  };
  const deleteVacancy = async (job: Vacancy) => {
    if (!canManage) return;
    if (
      !window.confirm(
        `Delete "${job.title}"? Application history will be retained.`
      )
    ) {
      return;
    }
    try {
      await api<ApiMessage>(
        `/admin/vacancies/${job._id}`,
        { method: "DELETE" },
        true
      );
      notify("Vacancy deleted successfully", "success");
      await loadVacancies();
    } catch (error) {
      reportError(error);
    }
  };
  const chooseCv = (file: File | null) => {
    if (!file) {
      setCv(null);
      return;
    }
    if (!ALLOWED_CV.test(file.name)) {
      notify("Unsupported CV format.", "warning");
      setCv(null);
      setFileKey((value) => value + 1);
      return;
    }
    if (file.size > MAX_CV_SIZE) {
      notify("Your CV must be 15 MB or smaller.", "warning");
      setCv(null);
      setFileKey((value) => value + 1);
      return;
    }
    setCv(file);
  };
  const applicantPhoneError = (() => {
    const digits = String(applicant.phone || "").replace(/\D/g, "");
    const localDigits = digits.replace(/^0+/, "");
    if (!digits) return "Phone number is required.";
    if (applicantPhoneCountryCode === "ZA" &&
        (localDigits.length !== 9 || !/^[6-8]\d{8}$/.test(localDigits))) {
      return "Enter a valid South African phone number.";
    }
    if (applicantPhoneCountryCode === "MW" &&
        (localDigits.length < 8 || localDigits.length > 9 || !/^[189]\d{7,8}$/.test(localDigits))) {
      return "Enter a valid Malawi phone number.";
    }
    return "";
  })();
  const submitApplication = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || saving) return;
    setApplicantPhoneTouched(true);
    if (applicantPhoneError) {
      notify(applicantPhoneError, "warning");
      return;
    }
    if (!cv) {
      notify("Please attach your CV.", "warning");
      return;
    }
    setSaving(true);
    try {
      const body = new FormData();
      body.set("fullName", applicant.fullName.trim());
      body.set("email", applicant.email.trim());
      body.set("phone", `+${buildInternationalNumber(applicantPhoneCountryCode, applicant.phone).replace(/^\+/, "")}`);
      body.set("coverLetter", applicant.coverLetter);
      body.set("cv", cv);
      const result = await api<ApiMessage>(
        `/vacancies/${selected._id}/apply`,
        { method: "POST", body }
      );
      notify(
        "Application received successfully!",
        "success",
        result.confirmationSent === false
          ? "Your application was received. We will contact you if shortlisted."
          : "Your application was received. A confirmation email has been sent."
      );
      setApplicant({ ...INITIAL_APPLICANT });
      setApplicantPhoneCountryCode("MW");
      setApplicantPhoneTouched(false);
      setCv(null);
      setFileKey((value) => value + 1);
      setSelected(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      reportError(error);
    } finally {
      setSaving(false);
    }
  };
  const updateApplication = async (
    application: Application,
    status: ApplicationStatus
  ) => {
    if (!canManage) return;
    try {
      await api<ApiMessage>(
        `/admin/applications/${application._id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        },
        true
      );
      notify("Application updated successfully", "success");
      await loadApplications();
    } catch (error) {
      reportError(error);
    }
  };
  const vacancyDetails = (job: Vacancy) => (
    <Stack spacing={5}>
      <HStack flexWrap="wrap" spacing={2}>
        <Badge colorScheme="green" px={3} py={1}>
          Now hiring
        </Badge>
        <Badge px={3} py={1}>{job.workMode}</Badge>
        <Badge px={3} py={1}>{job.employmentType}</Badge>
      </HStack>
      <Heading size="lg">{job.title}</Heading>
      <Text color="gray.600">
        {job.department} · {job.location}
      </Text>
      <Text fontSize="sm" color="gray.500">
        Posted {dateLabel(job.postedAt)}
        {job.closingDate && ` · Closes ${dateLabel(job.closingDate)}`}
      </Text>
      <Divider borderColor={BORDER} />
      <Box>
        <Heading size="sm" mb={3}>Role overview</Heading>
        <Text whiteSpace="pre-wrap" lineHeight="1.9">
          {job.summary}
        </Text>
      </Box>
      {!!job.compensation && (
        <Box>
          <Heading size="sm" mb={3}>Compensation</Heading>
          <Text whiteSpace="pre-wrap" lineHeight="1.9">
            {job.compensation}
          </Text>
        </Box>
      )}
      {job.responsibilities?.length > 0 && (
        <Box>
          <Heading size="sm" mb={3}>Key responsibilities</Heading>
          <Box as="ul" pl={5}>
            {job.responsibilities.map((item, index) => (
              <Box as="li" key={index} mb={2} lineHeight="1.8">
                {item}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {job.requirements?.length > 0 && (
        <Box>
          <Heading size="sm" mb={3}>Requirements</Heading>
          <Box as="ul" pl={5}>
            {job.requirements.map((item, index) => (
              <Box as="li" key={index} mb={2} lineHeight="1.8">
                {item}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {!!job.workArrangement && (
        <Box>
          <Heading size="sm" mb={3}>Work arrangement</Heading>
          <Text whiteSpace="pre-wrap" lineHeight="1.9">
            {job.workArrangement}
          </Text>
        </Box>
      )}
    </Stack>
  );
  const renderCard = (job: Vacancy, management = false) => (
    <Box
      key={job._id}
      bg="white"
      border="1px solid"
      borderColor={BORDER}
      borderRadius={RADIUS}
      p={{ base: 4, md: 6 }}
      w="100%"
      minW={0}
      transition="border-color .2s, box-shadow .2s"
      _hover={{
        borderColor: "#9acdc5",
        shadow: "sm",
      }}
    >
      <Flex
        justify="space-between"
        align="start"
        gap={3}
        flexWrap="wrap"
      >
        <HStack spacing={2} flexWrap="wrap">
          <Badge colorScheme={statusColor(job.status)} px={3} py={1}>
            {job.status === "hiring" ? "Now hiring" : job.status}
          </Badge>
          <Badge px={3} py={1}>{job.workMode}</Badge>
          <Badge px={3} py={1}>{job.employmentType}</Badge>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          Posted {dateLabel(job.postedAt)}
        </Text>
      </Flex>
      <Heading size={{ base: "md", md: "lg" }} mt={4} mb={2}>
        {job.title}
      </Heading>
      <Text color="gray.600" fontSize="sm" mb={4}>
        {job.department} · {job.location}
      </Text>
      <Text
        color="gray.700"
        lineHeight="1.8"
        whiteSpace="pre-wrap"
        noOfLines={3}
        mb={5}
      >
        {job.summary ||
          (job.status === "draft"
            ? "Draft vacancy — complete the role overview to publish."
            : "")}
      </Text>
      <Divider borderColor={BORDER} mb={4} />
      <Flex
        justify="space-between"
        align="center"
        gap={3}
        flexWrap="wrap"
      >
        <Text color="gray.500" fontSize="sm">
          {job.closingDate
            ? `Closes ${dateLabel(job.closingDate)}`
            : job.status === "draft"
              ? "Unpublished draft"
              : "Open until filled"}
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          {canManage && (
            <>
              <Button
                size="sm"
                variant="outline"
                colorScheme="teal"
                onClick={() => editVacancy(job)}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={() => void deleteVacancy(job)}
              >
                Delete
              </Button>
            </>
          )}
          {management && canManage && (
            <>
              {job.status !== "hiring" ? (
                <Button
                  size="sm"
                  colorScheme="teal"
                  onClick={() => void changeStatus(job, "hiring")}
                >
                  Publish / Reopen
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void changeStatus(job, "paused")}
                >
                  Pause
                </Button>
              )}
              {job.status !== "closed" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void changeStatus(job, "closed")}
                >
                  Close
                </Button>
              )}
            </>
          )}
          {!management && job.status === "hiring" && (
<Button
  size="md"
  minW="150px"
  px={6}
  h="42px"
  bg={BRAND}
  color="white"
  fontSize="sm"
  fontWeight="600"
  borderRadius="5px"
  display="flex"
  alignItems="center"
  justifyContent="center"
  textAlign="center"
  _hover={{ bg: BRAND_DARK }}
  onClick={() => {
    setSelected(job);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }}
>
  View & apply →
</Button>
          )}
        </HStack>
      </Flex>
    </Box>
  );
  return (
    <Box
      minH="80vh"
      bg={BG}
      color="#30333a"
      py={{ base: 5, md: 7 }}
      sx={{
        "& .chakra-button:not(.chakra-modal__close-btn)": {
          minWidth: "150px",
          minHeight: "42px",
          paddingInline: "24px",
          fontSize: "14px",
          fontWeight: 600,
          borderRadius: RADIUS,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          whiteSpace: "nowrap",
        },
        "& input, & textarea, & select, & [role='tab']": {
          borderRadius: RADIUS,
        },
        "& .chakra-badge": {
          borderRadius: RADIUS,
        },
      }}
    >
      <Container maxW="100%" w="100%" px={{ base: 3, md: 2, xl: 8 }}>
        {/* VACANCY EDITOR */}
        {canManage && (
          <Modal
            isOpen={modal.isOpen}
            onClose={closeModal}
            size="4xl"
            isCentered
            scrollBehavior="inside"
            closeOnOverlayClick={!saving}
          >
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent
              mx={{ base: 2, md: 4 }}
              my={3}
              maxH="calc(100dvh - 24px)"
              borderRadius={RADIUS}
              display="flex"
              flexDirection="column"
              overflow="hidden"
              sx={{
                "& .chakra-button:not(.chakra-modal__close-btn)": {
          minWidth: "150px",
          minHeight: "42px",
          paddingInline: "24px",
          fontSize: "14px",
          fontWeight: 600,
          borderRadius: RADIUS,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          whiteSpace: "nowrap",
        },
                "& input, & textarea, & select": {
                  borderRadius: RADIUS,
                },
              }}
            >
              <ModalHeader
                bg={BG}
                flexShrink={0}
                pr={12}
                py={5}
                borderBottom="1px solid"
                borderColor={BORDER}
              >
                <Text color={BRAND} fontSize="xs" fontWeight="bold">
                  VACANCY MANAGEMENT
                </Text>
                <Heading size="md" mt={1}>
                  {editingId ? "Edit vacancy" : "Add vacancy"}
                </Heading>
                <Text
                  fontSize="sm"
                  color="gray.600"
                  fontWeight="normal"
                  mt={2}
                >
                  Complete the sections below or save a draft to continue later.
                </Text>
              </ModalHeader>
              <ModalCloseButton top={4} right={4} isDisabled={saving} />
              <Box
                as="form"
                display="flex"
                flexDirection="column"
                minH={0}
                flex="1"
                onSubmit={(event: React.FormEvent) => {
                  event.preventDefault();
                  void saveVacancy(false);
                }}
              >
                <ModalBody
                  bg={BG}
                  overflowY="auto"
                  minH={0}
                  flex="1"
                  px={{ base: 3, md: 6 }}
                  py={5}
                >
                  <Stack spacing={4}>
                    <Box {...cardStyle} p={5}>
                      <SectionTitle
                        number="01"
                        title="Position and job details"
                        description="Enter the position, location and employment details."
                      />
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl isRequired>
                          <FormLabel>Position title</FormLabel>
                          <Input
                            value={form.title}
                            onChange={(e) => update("title", e.target.value)}
                            placeholder="Sales Executive: Virtual/Remote"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Department</FormLabel>
                          <Input
                            value={form.department}
                            onChange={(e) => update("department", e.target.value)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Location</FormLabel>
                          <Input
                            value={form.location}
                            onChange={(e) => update("location", e.target.value)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Work mode</FormLabel>
                          <Select
                            value={form.workMode}
                            onChange={(e) =>
                              update("workMode", e.target.value as WorkMode)
                            }
                          >
                            <option value="remote">Remote</option>
                            <option value="hybrid">Hybrid</option>
                            <option value="onsite">On-site</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Employment type</FormLabel>
                          <Select
                            value={form.employmentType}
                            onChange={(e) =>
                              update(
                                "employmentType",
                                e.target.value as EmploymentType
                              )
                            }
                          >
                            <option value="full-time">Full-time</option>
                            <option value="part-time">Part-time</option>
                            <option value="contract">Contract</option>
                            <option value="internship">Internship</option>
                          </Select>
                        </FormControl>
                      </SimpleGrid>
                    </Box>
                    <Box {...cardStyle} p={5}>
                      <SectionTitle
                        number="02"
                        title="Compensation"
                        description="Salary, commission and benefits. Pasted formatting is preserved."
                      />
                      <Textarea
                        minH="140px"
                        resize="vertical"
                        whiteSpace="pre-wrap"
                        value={form.compensation}
                        onChange={(e) => update("compensation", e.target.value)}
                        placeholder={
                          "- Basic Salary: MK400,000 per month\n- Commission: Based on closed deals."
                        }
                      />
                    </Box>
                    <Box {...cardStyle} p={5}>
                      <SectionTitle
                        number="03"
                        title="Role overview"
                        description="Describe the position in paragraphs."
                      />
                      <FormControl isRequired={form.status === "hiring"}>
                        <Textarea
                          minH="180px"
                          resize="vertical"
                          whiteSpace="pre-wrap"
                          value={form.summary}
                          onChange={(e) => update("summary", e.target.value)}
                          placeholder="We are looking for a motivated candidate..."
                        />
                      </FormControl>
                    </Box>
                    <Box {...cardStyle} p={5}>
                      <SectionTitle
                        number="04"
                        title="Key responsibilities"
                        description="Paste each responsibility on a separate line."
                      />
                      <Textarea
                        minH="210px"
                        resize="vertical"
                        value={form.responsibilities}
                        onChange={(e) =>
                          update("responsibilities", e.target.value)
                        }
                        placeholder={
                          "- Develop new business opportunities.\n- Manage the sales pipeline."
                        }
                      />
                    </Box>
                    <Box {...cardStyle} p={5}>
                      <SectionTitle
                        number="05"
                        title="Requirements"
                        description="Enter the candidate requirements, one per line."
                      />
                      <Textarea
                        minH="210px"
                        resize="vertical"
                        value={form.requirements}
                        onChange={(e) =>
                          update("requirements", e.target.value)
                        }
                        placeholder={
                          "- Proven sales experience.\n- Strong communication skills."
                        }
                      />
                    </Box>
                    <Box {...cardStyle} p={5}>
                      <SectionTitle
                        number="06"
                        title="Work arrangement"
                        description="Explain remote work, client visits and expectations."
                      />
                      <Textarea
                        minH="160px"
                        resize="vertical"
                        whiteSpace="pre-wrap"
                        value={form.workArrangement}
                        onChange={(e) =>
                          update("workArrangement", e.target.value)
                        }
                      />
                    </Box>
                    <Box {...cardStyle} p={5}>
                      <SectionTitle
                        number="07"
                        title="Publishing settings"
                        description="Save as draft or publish to accept applications."
                      />
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                          <FormLabel>Vacancy status</FormLabel>
                          <Select
                            value={form.status}
                            onChange={(e) =>
                              update("status", e.target.value as Status)
                            }
                          >
                            <option value="draft">Draft</option>
                            <option value="hiring">Hiring / Open</option>
                            <option value="paused">Paused</option>
                            <option value="closed">Closed</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Closing date (optional)</FormLabel>
                          <Input
                            type="date"
                            value={form.closingDate}
                            onChange={(e) =>
                              update("closingDate", e.target.value)
                            }
                          />
                        </FormControl>
                      </SimpleGrid>
                    </Box>
                  </Stack>
                </ModalBody>
                <ModalFooter
                  bg="white"
                  borderTop="1px solid"
                  borderColor={BORDER}
                  flexShrink={0}
                  gap={2}
                  flexWrap="wrap"
                  px={{ base: 3, md: 6 }}
                >
                  <Button variant="ghost" isDisabled={saving} onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    colorScheme="teal"
                    isLoading={saving}
                    onClick={() => void saveVacancy(true)}
                  >
                    Save draft
                  </Button>
                  <Button
                    type="submit"
                    bg={BRAND}
                    color="white"
                    _hover={{ bg: BRAND_DARK }}
                    isLoading={saving}
                  >
                    {editingId
                      ? "Save changes"
                      : form.status === "hiring"
                        ? "Publish vacancy"
                        : "Create vacancy"}
                  </Button>
                </ModalFooter>
              </Box>
            </ModalContent>
          </Modal>
        )}
        {isAdminView ? (
          canManage ? (
            <Tabs colorScheme="teal" variant="enclosed" isLazy>
              <Flex
                justify="space-between"
                align="center"
                gap={3}
                flexWrap="wrap"
                mb={4}
              >
                <TabList>
                  <Tab>Vacancies</Tab>
                  <Tab>Applications</Tab>
                </TabList>
<Button
  size="md"
  minW="150px"
  px={6}
  h="42px"
  bg={BRAND}
  color="white"
  fontSize="sm"
  fontWeight="600"
  borderRadius="5px"
  display="flex"
  alignItems="center"
  justifyContent="center"
  textAlign="center"
  _hover={{ bg: BRAND_DARK }}
  onClick={openCreate}
>
  + Add vacancy
</Button>
              </Flex>
              <TabPanels>
                <TabPanel px={0}>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
                    {(["hiring", "draft", "paused", "closed"] as const).map(
                      (status) => (
                        <Box key={status} {...cardStyle} p={4}>
                          <Stat>
                            <StatLabel textTransform="capitalize">
                              {status}
                            </StatLabel>
                            <StatNumber>
                              {vacancies.counts?.[status] || 0}
                            </StatNumber>
                          </Stat>
                        </Box>
                      )
                    )}
                  </SimpleGrid>
                  <Flex
                    justify="space-between"
                    align="center"
                    gap={3}
                    flexWrap="wrap"
                    mb={4}
                  >
                    <Heading size="md">
                      All vacancies ({vacancies.total})
                    </Heading>
                    <Select
                      maxW="190px"
                      bg="white"
                      value={filter}
                      onChange={(e) => {
                        setFilter(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="">All statuses</option>
                      <option value="hiring">Hiring</option>
                      <option value="draft">Draft</option>
                      <option value="paused">Paused</option>
                      <option value="closed">Closed</option>
                    </Select>
                  </Flex>
                  {loading || (!vacanciesLoaded && !vacanciesError) ? (
                    <CareersLoader label="Loading vacancies..." />
                  ) : vacanciesError ? (
                    <Box {...cardStyle} p={8} textAlign="center"><Text>Could not load vacancies. Please try again.</Text><Button mt={3} onClick={() => void loadVacancies()}>Retry</Button></Box>
                  ) : (
                    <Stack spacing={4}>
                      {vacancies.items.map((job) => renderCard(job, true))}
                    </Stack>
                  )}
                  {!loading && vacanciesLoaded && !vacanciesError && vacancies.items.length === 0 && (
                    <Box {...cardStyle} p={10} textAlign="center">
                      <Text>No vacancies found.</Text>
                    </Box>
                  )}
                  {!loading && !vacanciesError && <Pagination
                    page={page}
                    pages={vacancies.pages}
                    change={setPage}
                  />}
                </TabPanel>
                <TabPanel px={0}>
                  <Heading size="md" mb={5}>
                    Applications ({applications.total})
                  </Heading>
                  {applicationLoading || (!applicationsLoaded && !applicationsError) ? (
                    <CareersLoader label="Loading applications..." />
                  ) : applicationsError ? (
                    <Box {...cardStyle} p={8} textAlign="center"><Text>Could not load applications. Please try again.</Text><Button mt={3} onClick={() => void loadApplications()}>Retry</Button></Box>
                  ) : (
                    <Stack spacing={3}>
                      {applications.items.map((application) => (
                        <Box key={application._id} {...cardStyle} p={5}>
                          <Flex
                            justify="space-between"
                            gap={4}
                            align="start"
                            flexWrap="wrap"
                          >
                            <Stack spacing={1} flex="1" minW="220px">
                              <Heading size="sm">{application.fullName}</Heading>
                              <Text fontSize="sm">{application.vacancyTitle}</Text>
                              <Text fontSize="sm" color="gray.600">
                                {application.email} · {application.phone}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Applied {dateLabel(application.createdAt)}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                CV: {application.cvName} (sent by email)
                              </Text>
                              {application.coverLetter && (
                                <Text mt={2} fontSize="sm" whiteSpace="pre-wrap">
                                  {application.coverLetter}
                                </Text>
                              )}
                            </Stack>
                            <Select
                              size="sm"
                              maxW="180px"
                              value={application.status}
                              onChange={(e) =>
                                void updateApplication(
                                  application,
                                  e.target.value as ApplicationStatus
                                )
                              }
                            >
                              {(
                                [
                                  "new",
                                  "reviewing",
                                  "shortlisted",
                                  "interview",
                                  "rejected",
                                  "hired",
                                ] as const
                              ).map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </Select>
                          </Flex>
                        </Box>
                      ))}
                    </Stack>
                  )}
                  {!applicationLoading && applicationsLoaded && !applicationsError && applications.total === 0 && (
                    <Box {...cardStyle} p={8} textAlign="center">
                      <Text>No applications yet.</Text>
                    </Box>
                  )}
                  {!applicationLoading && !applicationsError && <Pagination
                    page={applicationPage}
                    pages={applications.pages}
                    change={setApplicationPage}
                  />}
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            <Box {...cardStyle} p={8} textAlign="center">
              {!roleChecked ? <CareersLoader label="Verifying access..." /> : <Text>Super Admin access required.</Text>}
            </Box>
          )
        ) : selected ? (
          <SimpleGrid
            columns={{ base: 1, xl: 2 }}
            spacing={5}
            alignItems="start"
          >
            <Box {...cardStyle} p={{ base: 4, md: 7 }}>
              <Button
                size="sm"
                variant="ghost"
                mb={5}
                onClick={() => setSelected(null)}
              >
                ← Back to vacancies
              </Button>
              {vacancyDetails(selected)}
            </Box>
            <Box
              {...cardStyle}
              p={{ base: 4, md: 7 }}
              position={{ xl: "sticky" }}
              top={{ xl: "20px" }}
            >
              <Heading size="md" mb={2}>
                Apply for this position
              </Heading>
              <Text color="gray.600" fontSize="sm" mb={6}>
                Complete your details and attach your CV. We will contact
                you if you are shortlisted.
              </Text>
              <Box as="form" onSubmit={submitApplication}>
                <Stack spacing={5}>
                  <FormControl isRequired>
                    <FormLabel>Full name</FormLabel>
                    <Input
                      size="lg"
                      value={applicant.fullName}
                      maxLength={160}
                      placeholder="Your full name"
                      onChange={(e) =>
                        setApplicant((previous) => ({
                          ...previous,
                          fullName: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Email address</FormLabel>
                    <Input
                      size="lg"
                      type="email"
                      value={applicant.email}
                      placeholder="you@example.com"
                      onChange={(e) =>
                        setApplicant((previous) => ({
                          ...previous,
                          email: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                  <FormControl isRequired isInvalid={applicantPhoneTouched && Boolean(applicantPhoneError)}>
                    <AppPhoneInput
                      label="Phone number"
                      value={applicant.phone}
                      countryCode={applicantPhoneCountryCode}
                      placeholder="Phone number"
                      onCountryChange={(countryCode) => {
                        setApplicantPhoneCountryCode(countryCode);
                        setApplicant((previous) => ({ ...previous, phone: "" }));
                        setApplicantPhoneTouched(false);
                      }}
                      onNumberChange={(value) => {
                        const digits = String(value || "").replace(/\D/g, "");
                        const maxLength = digits.startsWith("0") ? 10 : 9;
                        setApplicant((previous) => ({ ...previous, phone: digits.slice(0, maxLength) }));
                      }}
                      onBlur={() => setApplicantPhoneTouched(true)}
                      theme={theme}
                      labelColor={theme.textMuted}
                      isRequired
                      isInvalid={applicantPhoneTouched && Boolean(applicantPhoneError)}
                    />
                    {applicantPhoneTouched && applicantPhoneError && (
                      <Text color="red.500" fontSize="sm" mt={1}>{applicantPhoneError}</Text>
                    )}
                  </FormControl>
                  <FormControl>
                    <FormLabel>Cover letter (optional)</FormLabel>
                    <Textarea
                      minH="110px"
                      resize="vertical"
                      maxLength={5000}
                      value={applicant.coverLetter}
                      placeholder="Briefly tell us why you are a good fit."
                      onChange={(e) =>
                        setApplicant((previous) => ({
                          ...previous,
                          coverLetter: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Upload your CV</FormLabel>
                    <Input
                      ref={uploadRef}
                      key={fileKey}
                      type="file"
                      display="none"
                      accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.jpg,.jpeg,.png"
                      onChange={(e) => chooseCv(e.target.files?.[0] || null)}
                    />
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label="Choose your CV file"
                      cursor="pointer"
                      textAlign="center"
                      border="1px dashed"
                      borderColor={cv ? BRAND : "#a9c6c2"}
                      borderRadius={RADIUS}
                      bg={cv ? "#eaf7f3" : "#f9fcfc"}
                      px={5}
                      py={7}
                      transition="all .2s"
                      _hover={{
                        bg: "#eff9f6",
                        borderColor: BRAND,
                      }}
                      onClick={() => uploadRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          uploadRef.current?.click();
                        }
                      }}
                    >
                      <Flex
                        w="48px"
                        h="48px"
                        bg="#e1f3ef"
                        color={BRAND}
                        borderRadius={RADIUS}
                        justify="center"
                        align="center"
                        mx="auto"
                        mb={3}
                        fontSize="2xl"
                      >
                        ↑
                      </Flex>
                      {cv ? (
                        <>
                          <Text fontWeight="bold" color={BRAND} noOfLines={2}>
                            {cv.name}
                          </Text>
                          <Text fontSize="sm" color="gray.600" mt={1}>
                            {formatBytes(cv.size)} · File selected
                          </Text>
                          <Text fontSize="sm" color={BRAND} mt={2}>
                            Click to replace
                          </Text>
                        </>
                      ) : (
                        <>
                          <Text fontWeight="semibold" fontSize="sm">
                            Click to upload your CV
                          </Text>
                          <Text color="gray.500" fontSize="xs" mt={2}>
                            PDF, Word, ODT, RTF, TXT, JPG or PNG
                          </Text>
                          <Text color="gray.500" fontSize="xs" mt={1}>
                            Maximum 15 MB
                          </Text>
                        </>
                      )}
                    </Box>
                    {cv && (
                      <Button
                        size="xs"
                        mt={2}
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => {
                          setCv(null);
                          setFileKey((value) => value + 1);
                        }}
                      >
                        Remove CV
                      </Button>
                    )}
                  </FormControl>
                  <Button
                    type="submit"
                    size="lg"
                    w="100%"
                    bg={BRAND}
                    color="white"
                    _hover={{ bg: BRAND_DARK }}
                    isLoading={saving}
                    loadingText="Submitting"
                    isDisabled={!cv}
                  >
                    Submit application →
                  </Button>
                  <Text color="gray.500" fontSize="xs" textAlign="center">
                    Your information will only be used for recruitment.
                  </Text>
                </Stack>
              </Box>
            </Box>
          </SimpleGrid>
        ) : (
          <>
            {/* ONE COMPACT HEADING ROW - NO TOP HERO CARD */}
            <Flex
            mt="5"
              justify="space-between"
              align="center"
              gap={3}
              flexWrap="wrap"
              mb={5}
            >
<Box>
  <Heading
    size={{ base: "md", md: "lg" }}
    color="#30333a"
    mb={1}
  >
    Open positions
  </Heading>
  <Text
    fontSize={{ base: "sm", md: "md" }}
    color="gray.600"
  >
    Explore our latest career opportunities and find the right role
    to grow your career with PickDengu.
  </Text>
</Box>
              <HStack spacing={2} flexWrap="wrap">
                <Badge
                  colorScheme="teal"
                  px={3}
                  py={2}
                  fontSize="sm"
                  borderRadius={RADIUS}
                >
                  {loading || (!vacanciesLoaded && !vacanciesError) ? "Loading..." : `${vacancies.total} open positions`}
                </Badge>
                {canManage && (
                  <Button
                    size="sm"
                    bg={BRAND}
                    color="white"
                    _hover={{ bg: BRAND_DARK }}
                    borderRadius={RADIUS}
                    onClick={openCreate}
                  >
                    + Add vacancy
                  </Button>
                )}
              </HStack>
            </Flex>
            {loading || (!vacanciesLoaded && !vacanciesError) ? (
              <CareersLoader label="Loading open positions..." />
            ) : vacanciesError ? (
              <Box {...cardStyle} textAlign="center" px={5} py={12}>
                <Heading size="md" mb={3}>Unable to load vacancies</Heading>
                <Text color="gray.600">Please check your connection and try again.</Text>
                <Button mt={4} onClick={() => void loadVacancies()}>Retry</Button>
              </Box>
            ) : (
              <Stack spacing={4} w="100%">
                {vacancies.items
                  .filter((job) => job.status === "hiring")
                  .map((job) => renderCard(job))}
              </Stack>
            )}
            {!loading && vacanciesLoaded && !vacanciesError && vacancies.items.length === 0 && (
              <Box
                {...cardStyle}
                textAlign="center"
                px={5}
                py={12}
              >
                <Heading size="md" mb={3}>
                  No vacancies open right now
                </Heading>
                <Text color="gray.600">
                  Please check back for new opportunities at PickDengu.
                </Text>
              </Box>
            )}
            {!loading && !vacanciesError && <Pagination
              page={page}
              pages={vacancies.pages}
              change={setPage}
            />}
          </>
        )}
      </Container>
    </Box>
  );
}
